/**
 * Confirm and lead-select logic for scale drop form.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';
import type { Gateway, LeadsList, ShortLinkTld } from '@/hooks';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { MAX_SELECTED_TEXTS } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { executeScaleDropConfirm } from './scaleDropFormConfirm';

export interface UseScaleDropFormConfirmParams {
  amount: number | '';
  routeId: number;
  tldValue: string;
  executeAfter: string;
  selectedSenderNames: string[];
  selectedSmsTexts: string[];
  perDropSmsTexts: string[][] | null;
  perDropSenderNames: string[][] | null;
  leadsListWiserId: number;
  isMulti: boolean;
  mode: 'scale' | 'edit' | 'create-from-lists';
  editDropId: string | undefined;
  onCreateFromLists?: (p: unknown, r?: DropsWithStatsRow[]) => void | Promise<void>;
  createFromListsWiserId?: number;
  defaultWorkDate: string;
  titlePrefix: string;
  routeMatch: Gateway | null;
  tldMatch: ShortLinkTld | null;
  tlds: ShortLinkTld[];
  rows: DropsWithStatsRow[];
  leadsLists: LeadsList[];
  leadsMatch: LeadsList | null;
  allDropsHaveGateway: boolean;
  setLeadsListWiserIdTracked: (v: number | ((p: number) => number)) => void;
  onSave?: (p: {
    routeId: number;
    leadsListWiserId: number;
    shortLinkTldValue: string;
    amount: number;
    executeAfter?: string | null;
    senderNames: string[];
    smsTexts: string[];
    forceSend?: boolean;
    batchAmount?: number | null;
    batchDelay?: number | null;
    useOnlyNewDomains?: boolean;
    clickIdStructure?: ShortlinkClickIdStructure;
    domainScheme?: ShortlinkDomainScheme;
    staticTagId?: string | null;
  }) => void;
  onConfirm?: (p: unknown, r?: DropsWithStatsRow[]) => void | Promise<void>;
  forceSend: boolean;
  sendInBatches: boolean;
  batchAmount: number | undefined;
  batchDelay: number | undefined;
  useOnlyNewDomains: boolean;
  clickIdStructure: ShortlinkClickIdStructure;
  domainScheme: ShortlinkDomainScheme;
  scheduleIntervalMinutes: number;
  selectedStaticTagId: string | null;
}

export function useScaleDropFormConfirm(params: UseScaleDropFormConfirmParams) {
  const {
    amount,
    routeId,
    tldValue,
    executeAfter,
    selectedSenderNames,
    selectedSmsTexts,
    perDropSmsTexts,
    perDropSenderNames,
    leadsListWiserId,
    isMulti,
    mode,
    editDropId,
    defaultWorkDate,
    titlePrefix,
    routeMatch,
    tldMatch,
    tlds,
    rows,
    leadsLists,
    leadsMatch,
    setLeadsListWiserIdTracked,
    onSave,
    onConfirm,
    onCreateFromLists,
    createFromListsWiserId,
    forceSend,
    sendInBatches,
    batchAmount,
    batchDelay,
    useOnlyNewDomains,
    clickIdStructure,
    domainScheme,
    scheduleIntervalMinutes,
    selectedStaticTagId,
  } = params;

  const handleConfirm = useCallback(
    (onClose: () => void) =>
      executeScaleDropConfirm({
        amount,
        routeId,
        tldValue,
        executeAfter,
        selectedSenderNames,
        selectedSmsTexts,
        perDropSmsTexts,
        perDropSenderNames,
        leadsListWiserId: leadsListWiserId ?? 0,
        isMulti,
        mode,
        editDropId,
        defaultWorkDate,
        titlePrefix,
        routeMatch: routeMatch ?? null,
        tldMatch: tldMatch ?? null,
        tlds,
        rows,
        onSave,
        onConfirm,
        onCreateFromLists,
        onClose,
        forceSend,
        batchAmount: sendInBatches ? batchAmount : undefined,
        batchDelay: sendInBatches ? batchDelay : undefined,
        useOnlyNewDomains,
        clickIdStructure,
        domainScheme,
        scheduleIntervalMinutes,
        selectedStaticTagId,
      }),
      [
      amount, routeId, tldValue, executeAfter,
      selectedSenderNames, selectedSmsTexts, perDropSmsTexts, perDropSenderNames, leadsListWiserId,
      isMulti, mode, editDropId, defaultWorkDate, titlePrefix,
      routeMatch, tldMatch, tlds, rows, onSave, onConfirm, onCreateFromLists,
      forceSend, sendInBatches, batchAmount, batchDelay,
      useOnlyNewDomains, clickIdStructure, domainScheme, scheduleIntervalMinutes, selectedStaticTagId,
    ],
  );

  const createFromListsValid = mode === 'create-from-lists' && (createFromListsWiserId ?? 0) > 0;
  const canConfirm =
    mode === 'create-from-lists' && !createFromListsValid
      ? false
      : isMulti
        ? amount !== '' && amount >= 1 && (params.allDropsHaveGateway || routeId > 0)
        : Boolean(
            routeId &&
              tldValue &&
              amount !== '' &&
              amount >= 1 &&
              selectedSenderNames.length > 0 &&
              selectedSmsTexts.length > 0 &&
              selectedSmsTexts.length <= MAX_SELECTED_TEXTS &&
              leadsListWiserId,
          );

  const defaultLeadsListId = leadsMatch?.id ?? leadsLists[0]?.id ?? 0;
  const handleLeadSelect = useCallback(
    (id: number) =>
      setLeadsListWiserIdTracked((prev) => (prev === id ? defaultLeadsListId : id)),
    [defaultLeadsListId, setLeadsListWiserIdTracked],
  );

  return { handleConfirm, canConfirm, handleLeadSelect, defaultLeadsListId };
}
